import React, { useState } from 'react';
import { 
    Dices, Trophy, Calendar, Users, BarChart3, 
    ChevronRight, Check, Star, Sparkles, ArrowRight,
    Mail, Loader2
} from 'lucide-react';

/** Build date from CRA env (set at `npm run build`); fallback = today (dev) */
function getReleaseDateLabel() {
    const iso = process.env.REACT_APP_BUILD_DATE || new Date().toISOString().slice(0, 10);
    const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
}

/**
 * LandingPage - Public-facing landing page for the SaaS platform
 */
const LandingPage = ({ onStartOnboarding }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const releaseDateLabel = getReleaseDateLabel();
    const copyrightYear = new Date().getFullYear();

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setError('Anna toimiva sähköpostiosoite');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const res = await fetch('/api/onboarding/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            
            if (res.ok) {
                setSent(true);
            } else {
                const data = await res.json();
                setError(data.error || 'Jokin meni pieleen');
            }
        } catch (err) {
            setError('Yhteysvirhe. Yritä uudelleen.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="landing-page">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <div className="hero-badge">
                        <Sparkles size={14} /> Uusin versio {releaseDateLabel}
                    </div>
                    <h1>
                        Lautapeliklubisi ansaitsee
                        <span className="gradient-text"> paremman alustan</span>
                    </h1>
                    <p className="hero-subtitle">
                        Lopeta WhatsApp-säätö ja Excel-taulukot. 
                        Hallitse liigaa, sovi peliajat ja seuraa tilastoja yhdessä paikassa.
                    </p>
                    
                    {/* Email Signup */}
                    {!sent ? (
                        <form className="signup-form" onSubmit={handleEmailSubmit}>
                            <div className="input-wrapper">
                                <Mail size={18} />
                                <input 
                                    type="email" 
                                    placeholder="anna@esimerkki.fi"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                            </div>
                            <button type="submit" disabled={loading}>
                                {loading ? <Loader2 size={18} className="spin" /> : 'Aloita ilmaiseksi'}
                                {!loading && <ChevronRight size={18} />}
                            </button>
                        </form>
                    ) : (
                        <div className="success-message">
                            <Check size={20} />
                            <span>Tarkista sähköpostisi! Lähetimme sinulle vahvistuskoodin.</span>
                        </div>
                    )}
                    {error && <p className="error-text">{error}</p>}
                    
                    <div className="hero-trust">
                        <span>✓ Ilmainen käyttö</span>
                        <span>✓ Ei luottokorttia</span>
                        <span>✓ Valmis 2 minuutissa</span>
                    </div>
                </div>
                
                <div className="hero-visual">
                    <div className="app-preview">
                        <div className="preview-header">
                            <div className="preview-dots">
                                <span></span><span></span><span></span>
                            </div>
                            <span>Peliklubi</span>
                        </div>
                        <div className="preview-content">
                            <div className="preview-leaderboard">
                                <div className="preview-row gold">
                                    <Trophy size={16} /> Masa - 142 MP
                                </div>
                                <div className="preview-row silver">
                                    <span>2.</span> Jari - 128 MP
                                </div>
                                <div className="preview-row bronze">
                                    <span>3.</span> Henkka - 115 MP
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features">
                <h2>Kaikki mitä tarvitset lautapeliliigan pyörittämiseen</h2>
                <div className="features-grid">
                    <FeatureCard 
                        icon={Trophy}
                        title="Reaaliaikainen ranking"
                        description="Dynaaminen pistejärjestelmä, joka huomioi pelaajamäärän ja vastustajien tason."
                    />
                    <FeatureCard 
                        icon={Calendar}
                        title="Doodle-tyylinen aikataulutus"
                        description="Ehdota aikoja, äänestä sopivista ja saa AI-suosituksia historian perusteella."
                    />
                    <FeatureCard 
                        icon={BarChart3}
                        title="Kattavat tilastot"
                        description="13 eri tilastokategoriaa: voittoprosentit, peliajat, ennätykset ja paljon muuta."
                    />
                    <FeatureCard 
                        icon={Users}
                        title="Pelaajahallinta"
                        description="Kutsu jäseniä magic link -kirjautumisella. Ei salasanoja muistettavaksi."
                    />
                    <FeatureCard 
                        icon={Dices}
                        title="BGG-integraatio"
                        description="Hae pelitiedot automaattisesti BoardGameGeekistä. Kuvat, säännöt ja arvostelut."
                    />
                    <FeatureCard 
                        icon={Sparkles}
                        title="AI-avustaja"
                        description="Kysy pelisääntöjä, pyydä setup-ohjeita tai analysoi tilastoja keskustelemalla."
                    />
                </div>
            </section>

            {/* Social Proof */}
            <section className="social-proof">
                <div className="testimonial">
                    <div className="stars">
                        {[1,2,3,4,5].map(i => <Star key={i} size={20} fill="#f59e0b" stroke="#f59e0b" />)}
                    </div>
                    <blockquote>
                        "Ennen käytettiin WhatsAppia ja Exceliä. Nyt kaikki on yhdessä paikassa ja 
                        peli-iltojen sopiminen on helppoa."
                    </blockquote>
                    <cite>— Pelaaja, Peliklubi</cite>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <h2>Valmis aloittamaan?</h2>
                <p>Luo oma klubisi ilmaiseksi muutamassa minuutissa.</p>
                <button className="cta-button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Luo klubi nyt <ArrowRight size={18} />
                </button>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <Dices size={24} /> Board Game League
                    </div>
                    <div className="footer-links">
                        <a href="#features">Ominaisuudet</a>
                        <a href="#pricing">Hinnoittelu</a>
                        <a href="#contact">Yhteystiedot</a>
                    </div>
                    <p>© {copyrightYear} Proseduuri Oy. Kaikki oikeudet pidätetään.</p>
                </div>
            </footer>

            <style>{`
                .landing-page {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
                    color: #f0f6fc;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                /* Hero */
                .hero {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 4rem;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 4rem 2rem;
                    min-height: 80vh;
                    align-items: center;
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: rgba(139, 92, 246, 0.15);
                    border: 1px solid rgba(139, 92, 246, 0.3);
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    color: #a78bfa;
                    margin-bottom: 1.5rem;
                }

                .hero h1 {
                    font-size: 3.5rem;
                    font-weight: 800;
                    line-height: 1.1;
                    margin: 0 0 1.5rem 0;
                }

                .gradient-text {
                    background: linear-gradient(135deg, #8b5cf6, #ec4899, #f59e0b);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .hero-subtitle {
                    font-size: 1.25rem;
                    color: #9ca3af;
                    line-height: 1.6;
                    margin-bottom: 2rem;
                }

                .signup-form {
                    display: flex;
                    gap: 0.75rem;
                    margin-bottom: 1.5rem;
                }

                .input-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: rgba(255, 255, 255, 0.05);
                    border: 2px solid #374151;
                    border-radius: 12px;
                    padding: 0 1rem;
                    flex: 1;
                    transition: border-color 0.2s;
                }

                .input-wrapper:focus-within {
                    border-color: #8b5cf6;
                }

                .input-wrapper svg {
                    color: #6b7280;
                }

                .input-wrapper input {
                    flex: 1;
                    background: transparent;
                    border: none;
                    padding: 1rem 0;
                    color: #f0f6fc;
                    font-size: 1rem;
                    outline: none;
                }

                .signup-form button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0 2rem;
                    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    border: none;
                    border-radius: 12px;
                    color: white;
                    font-weight: 600;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .signup-form button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
                }

                .signup-form button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .success-message {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: rgba(34, 197, 94, 0.15);
                    border: 1px solid rgba(34, 197, 94, 0.3);
                    padding: 1rem 1.5rem;
                    border-radius: 12px;
                    color: #22c55e;
                    margin-bottom: 1.5rem;
                }

                .error-text {
                    color: #ef4444;
                    font-size: 0.9rem;
                    margin-top: -1rem;
                    margin-bottom: 1rem;
                }

                .hero-trust {
                    display: flex;
                    gap: 2rem;
                    font-size: 0.9rem;
                    color: #6b7280;
                }

                /* App Preview */
                .hero-visual {
                    display: flex;
                    justify-content: center;
                }

                .app-preview {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    overflow: hidden;
                    width: 100%;
                    max-width: 400px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
                }

                .preview-header {
                    background: #21262d;
                    padding: 0.75rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    border-bottom: 1px solid #30363d;
                }

                .preview-dots {
                    display: flex;
                    gap: 6px;
                }

                .preview-dots span {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background: #30363d;
                }

                .preview-dots span:first-child { background: #ef4444; }
                .preview-dots span:nth-child(2) { background: #f59e0b; }
                .preview-dots span:last-child { background: #22c55e; }

                .preview-content {
                    padding: 1.5rem;
                }

                .preview-leaderboard {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .preview-row {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem 1rem;
                    background: rgba(255, 255, 255, 0.03);
                    border-radius: 8px;
                    font-weight: 500;
                }

                .preview-row.gold {
                    background: rgba(245, 158, 11, 0.15);
                    color: #f59e0b;
                }

                .preview-row.silver { color: #9ca3af; }
                .preview-row.bronze { color: #cd7f32; }

                /* Features */
                .features {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 4rem 2rem;
                }

                .features h2 {
                    text-align: center;
                    font-size: 2rem;
                    margin-bottom: 3rem;
                }

                .features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.5rem;
                }

                .feature-card {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    padding: 2rem;
                    transition: transform 0.2s, border-color 0.2s;
                }

                .feature-card:hover {
                    transform: translateY(-4px);
                    border-color: #8b5cf6;
                }

                .feature-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: rgba(139, 92, 246, 0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #a78bfa;
                    margin-bottom: 1rem;
                }

                .feature-card h3 {
                    margin: 0 0 0.5rem 0;
                    font-size: 1.1rem;
                }

                .feature-card p {
                    margin: 0;
                    color: #8b949e;
                    line-height: 1.6;
                }

                /* Social Proof */
                .social-proof {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 4rem 2rem;
                    text-align: center;
                }

                .testimonial {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid #30363d;
                    border-radius: 16px;
                    padding: 3rem;
                }

                .stars {
                    display: flex;
                    justify-content: center;
                    gap: 0.25rem;
                    margin-bottom: 1.5rem;
                }

                .testimonial blockquote {
                    font-size: 1.25rem;
                    font-style: italic;
                    margin: 0 0 1rem 0;
                    line-height: 1.6;
                }

                .testimonial cite {
                    color: #8b949e;
                    font-style: normal;
                }

                /* CTA */
                .cta-section {
                    text-align: center;
                    padding: 4rem 2rem;
                    background: linear-gradient(180deg, transparent, rgba(139, 92, 246, 0.1));
                }

                .cta-section h2 {
                    font-size: 2rem;
                    margin-bottom: 0.5rem;
                }

                .cta-section p {
                    color: #8b949e;
                    margin-bottom: 2rem;
                }

                .cta-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem 2rem;
                    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    border: none;
                    border-radius: 12px;
                    color: white;
                    font-weight: 600;
                    font-size: 1.1rem;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .cta-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
                }

                /* Footer */
                .landing-footer {
                    border-top: 1px solid #30363d;
                    padding: 2rem;
                }

                .footer-content {
                    max-width: 1200px;
                    margin: 0 auto;
                    text-align: center;
                }

                .footer-brand {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    font-weight: 600;
                    margin-bottom: 1rem;
                }

                .footer-links {
                    display: flex;
                    justify-content: center;
                    gap: 2rem;
                    margin-bottom: 1rem;
                }

                .footer-links a {
                    color: #8b949e;
                    text-decoration: none;
                    transition: color 0.2s;
                }

                .footer-links a:hover {
                    color: #f0f6fc;
                }

                .footer-content p {
                    color: #6b7280;
                    font-size: 0.85rem;
                }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* Responsive */
                @media (max-width: 968px) {
                    .hero {
                        grid-template-columns: 1fr;
                        text-align: center;
                        padding: 2rem 1rem;
                    }

                    .hero h1 {
                        font-size: 2.5rem;
                    }

                    .signup-form {
                        flex-direction: column;
                    }

                    .signup-form button {
                        justify-content: center;
                        padding: 1rem;
                    }

                    .hero-trust {
                        justify-content: center;
                        flex-wrap: wrap;
                        gap: 1rem;
                    }

                    .hero-visual {
                        order: -1;
                    }

                    .app-preview {
                        max-width: 320px;
                    }
                }
            `}</style>
        </div>
    );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="feature-card">
        <div className="feature-icon">
            <Icon size={24} />
        </div>
        <h3>{title}</h3>
        <p>{description}</p>
    </div>
);

export default LandingPage;

